import { inject, Injectable, signal } from "@angular/core";
import { filter, tap } from "rxjs";

import { Constants } from "../constants/constants";
import type { AggregationPerfModelType } from "../models";
import { CalculationEngineApiService } from "./calculation-engine-api-service";
import { KpisApiService } from "./kpis-api-service";

@Injectable({
  providedIn: "platform",
})
export class AggregationPerfService {
  private readonly _calcEngineApiService = inject(CalculationEngineApiService);
  private readonly _kpisApiService = inject(KpisApiService);

  overallAggregationPerf = signal<AggregationPerfModelType | undefined>(undefined);

  aggregationPerf = signal<AggregationPerfModelType[]>([]);
  hasKPIsWithIssue = signal<boolean>(false);

  public pmoAnnualPerformance(year: number) {
    return this._calcEngineApiService.getPMOPerformance({ period: year, isAnnual: true }).pipe(
      tap({
        next: (response) => this._setPmoPerformance(response.responseData, response.inError),
      })
    );
  }

  public pmoQuarterlyPerformance(period: number) {
    return this._calcEngineApiService.getPMOPerformance({ period, isAnnual: false }).pipe(
      tap({
        next: (response) => this._setPmoPerformance(response.responseData, response.inError),
      })
    );
  }

  public entityAnnualPerformance(year: number, entityId: number, isExternalOrParticipating: boolean = false) {
    return this._calcEngineApiService.getEntityPerformance({ entityId, period: year, isAnnual: true }).pipe(
      filter((res) => !res.inError),
      tap({
        next: (response) => this._setEntityPerformance(response.responseData, isExternalOrParticipating),
      })
    );
  }

  public entityQuarterlyPerformance(period: number, entityId: number, isExternalOrParticipating: boolean = false) {
    return this._calcEngineApiService.getEntityPerformance({ entityId, period, isAnnual: false }).pipe(
      filter((res) => !res.inError),
      tap({
        next: (response) => this._setEntityPerformance(response.responseData, isExternalOrParticipating),
      })
    );
  }

  public getCountAuditIssues(period: number, entityId: number) {
    return this._kpisApiService.getCountAuditIssues({ entityId, period }).pipe(
      filter((res) => !res.inError),
      tap({
        next: (response) => {
          if (response.inError) return;

          this.hasKPIsWithIssue.set(response.responseData > 0);
        },
      })
    );
  }

  private _setEntityPerformance(
    responseData: AggregationPerfModelType[] | undefined,
    isExternalOrParticipating: boolean
  ) {
    this.overallAggregationPerf.set(
      responseData?.find(({ aggregationType }) => aggregationType === Constants.CONSTANT_AGG_TYPES.ENTITY_OVERALL)
    );

    const matchesAggregation = ({ aggregationType }: AggregationPerfModelType) =>
      isExternalOrParticipating
        ? aggregationType === Constants.CONSTANT_AGG_TYPES.ENTITY_NTKPI
        : aggregationType !== Constants.CONSTANT_AGG_TYPES.ENTITY_OVERALL;

    this.aggregationPerf.set(responseData?.filter(matchesAggregation) ?? []);
  }

  private _setPmoPerformance(responseData: AggregationPerfModelType[] | undefined, inError: boolean) {
    if (inError) return;

    this.overallAggregationPerf.set(
      responseData?.find(({ aggregationType }) => aggregationType === Constants.CONSTANT_AGG_TYPES.PMO_OVERALL)
    );

    this.aggregationPerf.set(
      responseData?.filter(({ aggregationType }) => aggregationType !== Constants.CONSTANT_AGG_TYPES.PMO_OVERALL) ?? []
    );
  }
}
